import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { STATS, VALUES } from '@/lib/site';
import { useCountUp } from '@/hooks/useCountUp';
import * as Icons from 'lucide-react';

function StatTile({ stat, when }) {
  const value = useCountUp(stat.value, { when, duration: 2200 });
  const formatted = value.toLocaleString('en-IN');
  return (
    <div className="group relative rounded-3xl glass p-6 transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl">
      <div className="font-display text-4xl md:text-5xl font-bold text-maroon-700 dark:text-saffron-200">
        {formatted}
        <span className="text-saffron-500">{stat.suffix}</span>
      </div>
      <div className="mt-2 text-sm font-semibold uppercase tracking-[0.16em] text-foreground/80">
        {stat.label}
      </div>
      <div className="mt-1 text-sm text-foreground/55">{stat.sub}</div>
      <div className="absolute inset-x-6 bottom-0 h-px bg-gradient-to-r from-transparent via-saffron-500/40 to-transparent opacity-0 group-hover:opacity-100 transition" />
    </div>
  );
}

export default function About() {
  const statsRef = useRef(null);
  const inView = useInView(statsRef, { once: true, margin: '-80px' });

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32"
    >
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Left text */}
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="eyebrow"
            >
              Our Story · Est. Winter 2024
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="h-display mt-4 text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.08] text-maroon-700 dark:text-foreground"
            >
              A small circle of seekers,
              <br />
              <span className="italic font-serif text-saffron-600 dark:text-saffron-300">
                now a movement of thousands.
              </span>
            </motion.h2>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-8 space-y-5 text-lg leading-relaxed text-foreground/75 max-w-2xl"
            >
              <p>
                ADI FOUNDATION began on a winter morning in Delhi, 2018 — five friends, one cycle
                rickshaw, and 47 plates of dal-rice. We were tired of seeing hunger sit politely
                at the edges of our city.
              </p>
              <p>
                Today, we serve across <strong className="text-foreground">all of India</strong>, with
                active hubs in <strong>Delhi, Lucknow and Nagpur</strong>. We hold one belief stubbornly:
                that to serve a human being is to serve the divine residing within.
              </p>
            </motion.div>

            {/* Mission / Vision */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                {
                  k: 'Our Mission',
                  v: 'To deliver dignified, transparent and proximate aid to the most vulnerable communities of India — rooted in seva, scaled by trust.',
                },
                {
                  k: 'Our Vision',
                  v: 'A nation where no child goes to bed hungry, no woman is shut out of opportunity, and no village is forgotten by progress.',
                },
              ].map((b, i) => (
                <motion.div
                  key={b.k}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 * i }}
                  className="rounded-2xl border border-border/70 bg-card p-6"
                >
                  <p className="eyebrow">{b.k}</p>
                  <p className="mt-3 font-serif text-lg text-foreground/85">{b.v}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right values column */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative rounded-3xl border border-saffron-500/20 bg-gradient-to-br from-saffron-50 to-cream dark:from-maroon-900/40 dark:to-[#22141B] p-7 md:p-9 grain"
            >
              <p className="eyebrow">Our Values</p>
              <h3 className="h-display mt-3 text-3xl font-semibold text-maroon-700 dark:text-saffron-100">
                Four pillars that hold us steady.
              </h3>

              <div className="mt-7 space-y-4">
                {VALUES.map((v, i) => {
                  const Icon = Icons[v.icon] || Icons.Heart;
                  return (
                    <motion.div
                      key={v.title}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.08 * i }}
                      className="flex items-start gap-4 group"
                    >
                      <div className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-white dark:bg-[#2A1820] text-saffron-600 dark:text-saffron-300 shadow-sm group-hover:scale-105 group-hover:bg-saffron-500 group-hover:text-white transition">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-display text-xl font-semibold text-foreground">
                          {v.title}
                        </p>
                        <p className="text-sm text-foreground/65 leading-relaxed">{v.text}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Stats */}
        <div ref={statsRef} className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.08 * i }}
            >
              <StatTile stat={s} when={inView} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
